import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store, Action } from '@ngrx/store';

import * as fromOrders from './order.reducer';
import * as OrdersSelectors from './order.selectors';
import * as OrdersActions from './order.actions';
import {
  IOrderLineItem,
  IGetOrdersQuery,
  IProductItem,
  DrawerType,
  OrderDirection,
  LoadType,
  IGetOrdersDTO,
  Prescription,
  IOrder
} from '@care-portals/api-interfaces';

import { APIService } from '../../services/api/api.service';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DrawerStateFacade } from '../drawer-state/drawer-state.facade';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { withOrgNavigationExtras } from '../app/layout-org.util';

@Injectable()
export class OrdersFacade {
  loadingOrders$ = this.store.pipe(select(OrdersSelectors.getLoadingOrders));
  exportingOrders$ = this.store.pipe(select(OrdersSelectors.getExportingOrders));
  savingOrder$ = this.store.pipe(select(OrdersSelectors.getSavingOrder));
  savingOrderId$ = this.store.pipe(select(OrdersSelectors.getSavingOrderId));
  orders$ = this.store.pipe(select(OrdersSelectors.selectOrders));
  loading$ = this.store.pipe(select(OrdersSelectors.getLoading));

  /* @deprecated  use activeOrder$ */
  orderResult$ = this.store.pipe(select(OrdersSelectors.getOrderResult));

  activeOrder$ = this.store.pipe(select(OrdersSelectors.getActiveOrder));
  activeOrderRxResults$ = this.store.pipe(
    select(OrdersSelectors.getActiveOrderPrescriptionsResults)
  );
  error$ = this.store.pipe(select(OrdersSelectors.getErrors));
  ordersCount$ = this.store.pipe(select(OrdersSelectors.getOrdersCount));
  hasMore$ = this.store.pipe(select(OrdersSelectors.hasMore));
  selectedId$ = this.store.pipe(select(OrdersSelectors.getSelectedId));

  constructor(
    private store: Store<fromOrders.OrdersPartialState>,
    private apiService: APIService,
    private readonly drawerFacade: DrawerStateFacade,
    private readonly notification: NzNotificationService,
    private readonly router: Router
  ) { }

  dispatch(action: Action) {
    this.store.dispatch(action);
  }

  clearActiveOrder() {
    this.dispatch(OrdersActions.clearActiveOrder());
  }

  loadOrder(orderId: number) {
    this.dispatch(OrdersActions.loadOrder({ orderId }));
  }

  loadOrders(loadType: LoadType, filters: IGetOrdersQuery | IGetOrdersDTO) {
    this.dispatch(OrdersActions.loadOrders({ loadType, filters }));
  }

  createOrder(
    orderItems: IOrderLineItem[],
    customerId: string,
    isRefill: boolean = false,
    delayedCapture: boolean = false,
    sendEvent: boolean = false
  ) {
    this.dispatch(
      OrdersActions.createOrder({ orderItems, customerId, isRefill, delayedCapture, sendEvent })
    );
  }

  updateOrder(orderId: number, data: Partial<any>) {
    this.dispatch(OrdersActions.updateOrder({ orderId, data }));
  }

  retriggerOrderStatus(orderId: number) {
    this.dispatch(OrdersActions.retriggerOrder({ orderId }));
  }
  retriggerPaymentStatus(orderId: number) {
    this.dispatch(OrdersActions.retriggerPayment({ orderId }));
  }
  updateOrderState(orderId: number, order: Partial<IOrder>) {
    const update = {
      id: orderId,
      changes: { ...order }
    };
    return this.dispatch(OrdersActions.updateOrderSuccess({ update }));
  }

  clearOrderResult() {
    this.dispatch(OrdersActions.clearOrderResult());
  }

  async chargeOrder(orderId: string, customerId: string) {
    this.dispatch(OrdersActions.chargeOrder({ orderId, customerId }));
  }

  async getOrderRequirements(orderId: number) {
    return await firstValueFrom(this.apiService.getOrderRequirements(orderId));
  }

  openBigcommerce(id: string) {
    // TODO need to make the store dynamic based on the org
    window.open(
      environment.bigcommerce_view_order_url.replace('{orderId}', id),
      '_blank'
    );
  }

  openStripe(id: string) {
    if (id.startsWith('pi_')) {
      window.open(`https://dashboard.stripe.com/payments/${id}`, '_blank');
    } else if (id.startsWith('in_')) {
      window.open(`https://dashboard.stripe.com/invoices/${id}`, '_blank');
    } else if (id.startsWith('ch_')) {
      window.open(`https://dashboard.stripe.com/charges/${id}`, '_blank');
    } else {
      window.open(`https://dashboard.stripe.com/customers`, '_blank');
    }
  }

  openTrackingLink(link: string) {
    window.open(link, '_blank');
  }

  openShipstation(id: string) {
    window.open(
      `https://ship14.shipstation.com/orders/all-orders-search-result?quickSearch=${id}`,
      '_blank'
    );
  }

  async refillItem(orderId: number, product: IProductItem) {
    return await firstValueFrom(this.apiService.refillItem(orderId, product));
  }

  openOrderDetails(orderId: string, onClose?: () => void) {
    this.router.navigate([], withOrgNavigationExtras({
      queryParams: { id: orderId },
    }));
    this.drawerFacade.openDrawer({
      name: DrawerType.ORDER_DETAILS,
      props: {
        orderId: orderId
      },
      onClose:
        onClose ??
        (() => {
          this.router.navigate([], withOrgNavigationExtras({
            queryParams: { id: null },
          }));
        })
    });
  }

  switchOrder(direction: OrderDirection) {
    this.store.dispatch(OrdersActions.switchOrder({ direction }));
  }

  updateOrderPrescription(orderId: string, prescription: Prescription) {
    this.store.dispatch(
      OrdersActions.updateOrderPrescriptionSuccess({ orderId, prescription })
    );
  }

  exportOrders(filters: IGetOrdersDTO) {
    this.store.dispatch(OrdersActions.exportOrders({ filters }));
  }
}
